import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useClearCache } from 'react-clear-cache';

import {
  AuthPartnersFooterV2,
  AuthSplitLayoutV2,
  InputV2,
  MicrosoftLoginButtonV2,
} from '../../../components/v2';
import useMicrosoftLogin from '../../../auth/useMicrosoftLogin';
import { isMicrosoftLoginEnabled } from '../../../auth/msalConfig';
import * as authActions from '../../../store/auth/actions';
import { hideLoader } from '../../../store/loader/actions';
import * as selectorsAuth from '../../../store/auth/reducer';
import NewPasswordForm from './forms/NewPasswordForm';

const schema = {
  email: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
    length: { maximum: 64 },
  },
  password: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
    length: { maximum: 128 },
  },
};

const Login = ({
  login,
  loginMicrosoft,
  hideLoader,
  auth,
  modeSetting,
  resolvedMode,
  onModeSettingChange,
}) => {
  const history = useHistory();
  const { isLatestVersion, emptyCacheStorage } = useClearCache();
  const {
    enabled: microsoftEnabled,
    ready: microsoftReady,
    loading: microsoftLoading,
    loginWithMicrosoft,
  } = useMicrosoftLogin();

  const [viewPassword, setViewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  useEffect(() => {
    hideLoader();
  }, [hideLoader]);

  useEffect(() => {
    if (!isLatestVersion) {
      try {
        emptyCacheStorage();
      } catch (e) {
        console.error('Erro ao limpar o cache', e);
      }
    }
  }, [isLatestVersion, emptyCacheStorage]);

  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState(prev => ({
      ...prev,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  const mustUpdatePassword = Boolean(auth?.updatePassword);

  if (auth?.token && !mustUpdatePassword && !showPasswordForm) {
    return <Redirect to="/v2/nsirs" />;
  }

  const handleChange = event => {
    event.persist();
    if (loginError) {
      setLoginError(null);
    }
    setFormState(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value,
      },
      touched: {
        ...prev.touched,
        [event.target.name]: true,
      },
    }));
  };

  const handleAuthSuccess = (shouldUpdatePassword, successLogin, errorMessage) => {
    setLoading(false);
    if (successLogin) {
      setLoginError(null);
      if (shouldUpdatePassword) {
        setShowPasswordForm(true);
      } else {
        history.push('/v2/nsirs');
      }
    } else {
      setLoginError(errorMessage || 'Erro ao logar');
    }
  };

  const handleLogin = () => {
    if (!isLatestVersion) {
      return;
    }

    setLoading(true);
    setLoginError(null);
    login(formState.values, handleAuthSuccess, { suppressErrorToast: true });
  };

  const handleMicrosoftLogin = async () => {
    if (!microsoftEnabled || !microsoftReady) {
      return;
    }

    const microsoftAuth = await loginWithMicrosoft();
    if (!microsoftAuth?.idToken) {
      return;
    }

    loginMicrosoft(microsoftAuth, handleAuthSuccess);
  };

  const hasError = field =>
    !!(formState.touched[field] && formState.errors[field]);

  const passwordHelperText = loginError
    || (hasError('password') ? formState.errors.password[0] : null);

  if (showPasswordForm || mustUpdatePassword) {
    return (
      <NewPasswordForm
        modeSetting={modeSetting}
        resolvedMode={resolvedMode}
        onModeSettingChange={onModeSettingChange}
      />
    );
  }

  const microsoftFeatureEnabled = isMicrosoftLoginEnabled();

  return (
    <AuthSplitLayoutV2
      footer={<AuthPartnersFooterV2 />}
      modeSetting={modeSetting}
      resolvedMode={resolvedMode}
      onModeSettingChange={onModeSettingChange}
    >
      <Box
        component="form"
        onSubmit={e => {
          e.preventDefault();
          if (formState.isValid) handleLogin();
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Typography variant="h5" fontWeight={700}>
              Bem-vindo de volta
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Informe suas credenciais para continuar
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <InputV2
              label="Usuário"
              name="email"
              isRequired
              error={hasError('email')}
              helperText={hasError('email') ? formState.errors.email[0] : null}
              value={formState.values.email || ''}
              onChange={handleChange}
            />

            <InputV2
              label="Senha"
              name="password"
              isRequired
              type={viewPassword ? 'text' : 'password'}
              error={hasError('password') || !!loginError}
              helperText={passwordHelperText}
              showHelperText={!!passwordHelperText}
              value={formState.values.password || ''}
              onChange={handleChange}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={viewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      onClick={() => setViewPassword(v => !v)}
                      edge="end"
                      size="small"
                    >
                      {viewPassword ? (
                        <VisibilityOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              color="primary"
              disabled={!formState.isValid || loading || !isLatestVersion}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Entrar'
              )}
            </Button>

            {/* <Divider>
              <Typography variant="overline" color="text.disabled">
                ou
              </Typography>
            </Divider>

            <MicrosoftLoginButtonV2
              disabled={!microsoftFeatureEnabled || !microsoftReady}
              loading={microsoftLoading}
              onClick={handleMicrosoftLogin}
              tooltipTitle={
                microsoftFeatureEnabled
                  ? ''
                  : 'Login Microsoft em breve — aguardando integração com o backend'
              }
            /> */}
          </Stack>
        </Stack>
      </Box>
    </AuthSplitLayoutV2>
  );
};

Login.propTypes = {
  login: PropTypes.func.isRequired,
  loginMicrosoft: PropTypes.func.isRequired,
  hideLoader: PropTypes.func.isRequired,
  auth: PropTypes.object,
  modeSetting: PropTypes.oneOf(['system', 'light', 'dark']),
  resolvedMode: PropTypes.oneOf(['light', 'dark']),
  onModeSettingChange: PropTypes.func,
};

const mapStateToProps = state => ({
  auth: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...authActions, hideLoader }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Login);
